import React, { FC, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { withOptimizely, WithOptimizelyProps } from 'shared/optimizely';
import useLiveRamp from 'shared/hooks/optimizely/useLiveRamp';
import Head from 'next/head';
import triggerTracking from '@blueprint/tracking';
import { IWindowWithLiveRamp, LiveRampCommands } from './IWindowWithLiveRamp';
import { addDays } from 'date-fns';
import { IConsent, ConsentType } from './ConsentTypes';

import defaultHeader from './assets/header.html.txt';
import experimentHeaderControl from './assets/header-CRME-1146-control.html.txt';
import experimentHeaderRegText from './assets/header-CRME-1146-reg_text.html.txt';
import experimentHeaderRegIcon from './assets/header-CRME-1146-reg_icon.html.txt';
import experimentHeaderRegButton from './assets/header-CRME-1146-reg_button.html.txt';
import experimentHeaderSignIcon from './assets/header-CRME-1146-sign_icon.html.txt';
import liveRampScript from './assets/gdpr-liveramp.js.txt';

const cookies = Cookies.withConverter({
    write: (value) => value.toString(),
    read: (value) => value
});
declare const window: IWindowWithLiveRamp;
const LiveRampVersion = 2;

const zooplaDomain = 'zoopla.co.uk';
const cookieConsentsKey = 'cookie_consents';

const createConsentObject = (): IConsent => ({
    schemaVersion: 4,
    content: {
        brand: 1,
        consents: [
            {
                apiVersion: 1,
                stored: false,
                date: new Date().toUTCString(),
                categories: [
                    {
                        id: ConsentType.Necessary,
                        consentGiven: false
                    },
                    {
                        id: ConsentType.Advertising,
                        consentGiven: false
                    },
                    {
                        id: ConsentType.Functionality,
                        consentGiven: false
                    }
                ]
            }
        ]
    }
});

const getCookieDomain = () => {
    const { hostname } = window.location;
    const isZooplaDomain = hostname.includes(zooplaDomain);

    return isZooplaDomain ? zooplaDomain : hostname;
};

const createConsentsCookie = () => {
    const cookieObject = createConsentObject();
    cookies.set(cookieConsentsKey, cookieObject, {
        path: '/',
        domain: getCookieDomain(),
        expires: addDays(Date.now(), 1)
    });
};

const cookieHasBeenCreated = () => {
    const consentCookie = cookies.get(cookieConsentsKey);

    if (!consentCookie) {
        return false;
    }

    try {
        const consent: IConsent = JSON.parse(consentCookie);

        const anyConsentsTrue = consent.content.consents[0].categories.some(
            (consent) => consent.consentGiven
        );

        return anyConsentsTrue ? false : true;
    } catch (error) {
        return false;
    }
};

const addLiveRampEventListener = () =>
    window.__tcfapi &&
    window.__tcfapi(
        LiveRampCommands.AddEventListener,
        LiveRampVersion,
        (tcData: unknown, success: boolean) => {
            console.info('LiveRamp event detected...');
            console.info('event: ', tcData, success);
        }
    );

const removeLiveRampEventListener = () =>
    window.__tcfapi &&
    window.__tcfapi(
        LiveRampCommands.RemoveEventListener,
        LiveRampVersion,
        (result: boolean) => {
            console.info(
                `LiveRamp listeners removed: ${result ? 'true' : 'false'}`
            );
        }
    );

const variationMap: Record<string, string> = {
    control: experimentHeaderControl,
    reg_text: experimentHeaderRegText,
    reg_icon: experimentHeaderRegIcon,
    reg_button: experimentHeaderRegButton,
    sign_icon: experimentHeaderSignIcon
};

const Header: FC<WithOptimizelyProps> = ({ optimizely }) => {
    const [cookieHasBeenSet, setCookieHasBeenSet] = useState<boolean>(() =>
        cookieHasBeenCreated()
    );

    const _useLiveRamp = useLiveRamp(optimizely);

    useEffect(() => {
        if (_useLiveRamp) {
            addLiveRampEventListener();
        }

        return () => {
            if (_useLiveRamp) {
                removeLiveRampEventListener();
            }
        };
    }, [_useLiveRamp]);

    useEffect(() => {
        if (_useLiveRamp && !cookieHasBeenSet) {
            createConsentsCookie();
            setCookieHasBeenSet(true);

            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'optimizely_feature',
                eventAction: 'liveramp-consent-banner',
                eventLabel: 'alternative'
            });
        }
    }, [_useLiveRamp, cookieHasBeenSet]);

    const optimizelyVariation = optimizely?.activate(
        'crme_1146_register_mobile_header_link'
    );

    let header = defaultHeader;

    if (optimizelyVariation && variationMap[optimizelyVariation]) {
        header = variationMap[optimizelyVariation];
    }

    return (
        <>
            {_useLiveRamp && (
                <Head>
                    <script
                        id="liveramp-tag"
                        dangerouslySetInnerHTML={{
                            __html: liveRampScript
                        }}
                    />
                </Head>
            )}
            <div
                data-testid="universal-header"
                dangerouslySetInnerHTML={{
                    __html: header
                }}
            />
        </>
    );
};

export default withOptimizely(Header);
